import axios from 'axios';
const API = axios.create({ baseURL: process.env.REACT_APP_API_URL });

// Attach Supabase auth token to all API requests
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('supabase_token') || 
    JSON.parse(localStorage.getItem('sb-' + (process.env.REACT_APP_SUPABASE_URL || '').split('//')[1]?.split('.')[0] + '-auth-token') || '{}')?.access_token;
  if (token) {
    config.headers.Authorization = 'Bearer ' + token;
  }
  return config;
});
export const getHealth = () => API.get('/health');
// Fix (2026-07-15): explicit limit/hours so All Signals / Trades Taken never
// silently re-fall back to the backend's old default cap (was limit=50,
// hours=24) as signal volume grows.
export const getSignals = (limit: number = 5000, hours: number = 720) => API.get(`/signals?limit=${limit}&hours=${hours}`);
// Fix (2026-07-15): explicit limit so Trades Taken / Performance KPIs never
// silently re-fall back to the backend's default cap as trade count grows.
export const getTrades = (limit: number = 500) => API.get(`/trades?limit=${limit}`);
export const getPositions = () => API.get('/positions');
export const getLivePositions = () => API.get('/positions/live');
// Fix 4 (2026-05-25): DB-authoritative open positions. Replaces
// getLivePositions in the dashboard; legacy endpoint kept for back-compat
// until any remaining callers are migrated.
export const getOpenPositions = () => API.get('/trading/open-positions');
export const getDailyPnl = () => API.get('/pnl/daily');
// Server-side risk_log filtering (2026-08-05): /risk/log accepts event,
// signal_class, and limit params so views aren't stuck client-filtering the
// default most-recent-50 window (MTF gate-eval rows flood it fast).
export type RiskLogParams = { event?: string; signal_class?: string; limit?: number };
export const getRiskLog = (params?: RiskLogParams) => API.get('/risk/log', { params });
export const getConsecStatus = () => API.get('/risk/consec-status');
export const getAuditLog = () => API.get('/audit/log');
export const getMissLog = () => API.get('/audit/misses');
export const getDeviations = () => API.get('/audit/deviations');
export const getStrategyCells = () => API.get('/cells');
export const approveCell = (id: string) => API.post(`/cells/${id}/approve`);
export const pauseCell = (id: string, reason?: string) => API.post(`/cells/${id}/pause`, { reason });
export const resumeCell = (id: string) => API.post(`/cells/${id}/resume`);
export const scaleCell = (id: string) => API.post(`/cells/${id}/scale`);
export const recomputeCells = () => API.post('/cells/recompute');
export const getWithdrawalRecommendation = (period: string = 'weekly') => API.get(`/withdrawals/recommendation?period=${period}`);
export const getWithdrawalsHistory = () => API.get('/withdrawals/history');
export const logWithdrawal = (amount: number, period: string, notes: string = '') =>
  API.post('/withdrawals/log', { amount, period, status: 'completed', notes });
export const resetAllData = () => API.post('/admin/reset');
export const getTradovateHealth = () => API.get('/tradovate/health');
export const getReviewSummary = (sample: string = 'all', source: string = 'all') =>
  API.get(`/review/summary?sample=${sample}&source=${source}`);
export const getNewsUpcoming = (hours: number = 24) => API.get(`/news/upcoming?hours=${hours}`);
export const getConnections = () => API.get('/connections');

// Trading Controls
export const getTradingStatus = () => API.get('/trading/status');
export const toggleKillSwitch = () => API.post('/trading/kill-switch');
export const toggleAutoExecute = () => API.post('/trading/autoexecute');
export const flattenAll = () => API.post('/trading/flatten');

export default API;

// Aliases and new functions for Settings controls
export const postKillSwitch = () => API.post('/trading/kill-switch');
export const postFlatten = () => API.post('/trading/flatten');
export const getSettings = () => API.get('/trading/status');
export const patchSettings = (data: Record<string, any>) => API.post('/settings/thresholds', data);

// TenniShue
export const getTenniShueStatus = () => API.get('/tennishue/status');
export const runTenniShue = () => API.get('/tennishue/run');

// Contract Scaling & Copy Trading
export const getCopyTradingStatus = () => API.get('/copy-trading/status');
export const activateCopyTrading = (accountId: string) => API.patch(`/accounts/${accountId}`, { is_active: true });
export const deactivateCopyTrading = (accountId: string) => API.patch(`/accounts/${accountId}`, { is_active: false });

export const getContractMode = () => API.get('/trading/contract');
export const patchContractMode = (mode: 'NQ' | 'MNQ') => API.patch('/trading/contract', { mode });
